export type AnimalSplitType =
  | 'whole'
  | 'half'
  | 'quarters'
  | 'half_and_two_quarters';

export const ALL_ANIMAL_SPLITS = [
  'whole',
  'half',
  'quarters',
  'half_and_two_quarters',
];

export const animalSplitTypeHelper = (value: AnimalSplitType | string) => {
  switch (value.toLowerCase()) {
    case 'whole':
      return { label: 'Whole', order: 0, value };

    case 'half':
      return { label: 'Half', order: 1, value };

    case 'quarters':
      return { label: 'Quarters', order: 2, value };

    case 'half_and_two_quarters':
      return { label: 'Half and Two Quarters', order: 3, value };

    default:
      return { label: value, order: 99, value };
  }
};

export type AnimalSplitPart = 'whole' | 'half' | 'quarter';

/**
 * What KIND of work a split represents, as opposed to what PORTION of an
 * animal it is (`AnimalSplitPart`) or how the carcass was divided
 * (`AnimalSplitType`). Absent means livestock — the overwhelming majority, and
 * the value every pre-existing split carries — so no backfill is needed and
 * every existing read keeps its current meaning.
 *
 * `game` marks a split created through the wild-game drop-off flow. It is
 * deliberately NOT expressed through the portion vocabularies: those values
 * feed invoice portion math, cut sheet assignment rules, and slot
 * materialization, all of which read them as fractions of a carcass.
 */
export type SplitKind = 'livestock' | 'game';
export const ALL_SPLIT_KINDS: SplitKind[] = ['livestock', 'game'];

/**
 * The species processed as WILD GAME — what a wild-game drop-off may be for.
 *
 * NOT the same as {@link WILD_GAME_ONLY_SPECIES}, and the difference matters:
 * `venison` shipped long before wild game did, and processors take farmed
 * venison today. It belongs here (a hunter's deer IS venison) but must never
 * be hidden, or enabling the module would REMOVE an existing species from
 * every processor not in the trial.
 */
export const GAME_SPECIES = [
  'venison',
  'elk',
  'moose',
  'antelope',
  'bear',
] as const;

export type GameSpecies = (typeof GAME_SPECIES)[number];

/**
 * The species the wild-game feature INTRODUCED, and therefore the only ones
 * safe to hide when the `WILD_GAME` module is off.
 *
 * Hiding is only ever safe for something that did not exist before the
 * feature. `venison` is deliberately absent: it predates wild game, so
 * stripping it would break processors who already have it enabled — their
 * species pickers would lose an option and an existing venison animal would
 * render with a value no longer in its own dropdown.
 *
 * The rule for anything added here later: if a processor could already have
 * the species on a record or in their settings, it does not belong in this
 * list.
 */
export const WILD_GAME_ONLY_SPECIES = [
  'elk',
  'moose',
  'antelope',
  'bear',
] as const;

/**
 * Whether a species is processed as wild game.
 * @param species the species value (case-insensitive).
 * @returns true when the species is a wild-game species.
 */
export function isGameSpecies(species?: string | null): boolean {
  if (!species) return false;
  return (GAME_SPECIES as readonly string[]).includes(species.toLowerCase());
}

export type AnimalClaimMode = 'direct' | 'request';
export const ALL_ANIMAL_CLAIM_MODES: AnimalClaimMode[] = ['direct', 'request'];

export type SplitStatus = 'open' | 'requested' | 'claimed' | 'confirmed';
export const ALL_SPLIT_STATUSES: SplitStatus[] = [
  'open',
  'requested',
  'claimed',
  'confirmed',
];

export const animalSplitPartHelper = (value: AnimalSplitPart | string) => {
  switch (value.toLowerCase()) {
    case 'whole':
      return { label: 'Whole', order: 0, value };

    case 'half':
      return { label: 'Half', order: 1, value };

    case 'quarter':
      return { label: 'Quarter', order: 2, value };

    default:
      return { label: value, order: 99, value };
  }
};

/**
 * Maps a `Split.splitPart` ('whole' | 'half' | 'quarter') into the
 * `AnimalSplitType` value space cutsheet assignment rules match on. The two
 * enums differ: a buyer claim records singular 'quarter' while a rule's
 * `splitType` filter uses the plural processor value 'quarters', and without
 * the mapping a size-specific rule never fires for a marketplace self-claim
 * (see `cutsheetRequirementDetailForSplit`).
 *
 * `half_and_two_quarters` is a whole-animal layout, so no `splitPart` maps to
 * it. Unknown/missing parts return `undefined` (the caller leaves `splitType`
 * unset so splitType-constrained rules are skipped).
 * @param splitPart - The buyer claim's split part ('whole' | 'half' | 'quarter').
 * @returns The matching `AnimalSplitType`, or `undefined` for unknown/missing parts.
 */
export function splitPartToSplitType(
  splitPart: AnimalSplitPart | string | null | undefined,
): AnimalSplitType | undefined {
  switch (splitPart?.toLowerCase()) {
    case 'whole':
      return 'whole';
    case 'half':
      return 'half';
    case 'quarter':
      return 'quarters';
    default:
      return undefined;
  }
}

/**
 * Whether a whole-animal layout `splitType` is consistent with a buyer's
 * per-claim `splitPart` — i.e. an animal laid out this way could actually
 * yield that portion. A mismatch (e.g. layout `whole`, claimed `half`) means
 * the `splitType` is a stale default from animal creation or a prior job
 * layout, not how this split is actually being sold.
 * @param splitType - The whole-animal layout value (plural value space).
 * @param splitPart - The buyer's per-claim split part.
 * @returns True when the layout could actually yield the claimed part.
 */
export function splitTypeMatchesSplitPart(
  splitType: AnimalSplitType | string | null | undefined,
  splitPart: AnimalSplitPart | string | null | undefined,
): boolean {
  const part = typeof splitPart === 'string' ? splitPart.toLowerCase() : '';
  switch (typeof splitType === 'string' ? splitType.toLowerCase() : '') {
    case 'whole':
      return part === 'whole';
    case 'half':
      return part === 'half';
    case 'quarters':
      return part === 'quarter';
    case 'half_and_two_quarters':
      return part === 'half' || part === 'quarter';
    default:
      return false;
  }
}

/**
 * The `splitType` a buyer's cutsheet/questionnaire selection should match for
 * a marketplace claim. The processor's layout (`animalSplitType`) wins only
 * when consistent with the buyer's claimed `splitPart`
 * ({@link splitTypeMatchesSplitPart}); otherwise the layout is a stale default
 * (e.g. a `whole` animal later sold in halves) and the size derived from the
 * buyer's `splitPart` wins — a half buyer is never shown the whole-animal
 * cutsheet. When the part can't be mapped it falls back to the raw layout, so
 * a previously-resolving case is never made worse.
 * @param animalSplitType - The processor's whole-animal layout for the animal.
 * @param splitPart - The buyer's claimed split part.
 * @returns The `splitType` the claim's cutsheet selection should match, if any.
 */
export function resolveClaimSplitType(
  animalSplitType: AnimalSplitType | string | null | undefined,
  splitPart: AnimalSplitPart | string | null | undefined,
): AnimalSplitType | undefined {
  if (
    animalSplitType &&
    splitTypeMatchesSplitPart(animalSplitType, splitPart)
  ) {
    return animalSplitType as AnimalSplitType;
  }
  return (
    splitPartToSplitType(splitPart) ??
    (animalSplitType as AnimalSplitType | undefined) ??
    undefined
  );
}

/**
 * The per-claim `splitPart`s that compose a whole-animal layout — the inverse
 * of {@link deriveLayoutFromSplits}. Used to materialize the open Split
 * placeholder slots for a layout so the layout is always recoverable from the
 * live split set (the source of truth). Unknown/missing layouts yield `[]`.
 * @param layout - The whole-animal layout value.
 * @returns The per-claim split parts that compose the layout.
 */
export function splitPartsForLayout(
  layout: AnimalSplitType | string | null | undefined,
): AnimalSplitPart[] {
  switch (typeof layout === 'string' ? layout.toLowerCase() : '') {
    case 'whole':
      return ['whole'];
    case 'half':
      return ['half', 'half'];
    case 'quarters':
      return ['quarter', 'quarter', 'quarter', 'quarter'];
    case 'half_and_two_quarters':
      return ['half', 'quarter', 'quarter'];
    default:
      return [];
  }
}

// Stable buyer-facing display order (whole → half → quarter).
const PART_ORDER: Record<string, number> = { whole: 0, half: 1, quarter: 2 };

/**
 * Map a processor's whole-animal layout `splitTypes` (the PLURAL value space:
 * whole|half|quarters|half_and_two_quarters) to the deduped, ordered set of
 * buyer-facing split PARTS (whole|half|quarter):
 *
 *   whole                  → whole
 *   half                   → half
 *   quarters               → quarter
 *   half_and_two_quarters  → half, quarter
 *
 * Uses {@link splitPartsForLayout} (the canonical layout→parts inverse) so the
 * derivation stays consistent with slot materialization. Unknown entries
 * contribute nothing. There is NO fallback to "all three" — an empty/omitted
 * `splitTypes` yields an empty part set.
 * @param splitTypes - The processor's whole-animal layout values for a species.
 * @returns The deduped buyer-facing split parts, in whole → half → quarter order.
 */
export function requestablePartsForSplitTypes(
  splitTypes: (string | null | undefined)[] | null | undefined,
): AnimalSplitPart[] {
  const parts = new Set<AnimalSplitPart>();
  for (const layout of splitTypes ?? []) {
    for (const part of splitPartsForLayout(layout)) {
      parts.add(part);
    }
  }
  return [...parts].sort(
    (a, b) => (PART_ORDER[a] ?? 99) - (PART_ORDER[b] ?? 99),
  );
}

/**
 * Minimal shape of a `ProcessorSettings.animalSettings[]` entry the split
 * derivation needs — kept loose so both the generated frontend type and the
 * Mongoose document satisfy it structurally.
 */
export interface AnimalSplitSettingLike {
  species?: string | null;
  splitTypes?: (string | null | undefined)[] | null;
}

/**
 * The buyer-facing split PARTS a processor offers for ONE species. Mirrors the
 * on-job `availableSplitTypes` derivation (splitTypes only — NO `isEnabled`
 * gate) so the producer listing picker and the buyer marketplace agree. An
 * empty/unknown species, or a species with no offered sizes, yields `[]`.
 * @param animalSettings - The processor's `animalSettings[]` entries.
 * @param species - The species to look up.
 * @returns The buyer-facing split parts offered for that species.
 */
export function offeredPartsForSpecies(
  animalSettings:
    | (AnimalSplitSettingLike | null | undefined)[]
    | null
    | undefined,
  species: string | null | undefined,
): AnimalSplitPart[] {
  if (!species) return [];
  const setting = (animalSettings ?? []).find((s) => s?.species === species);
  return requestablePartsForSplitTypes(setting?.splitTypes);
}

/**
 * Derive the whole-animal layout from the animal's live Split docs — the
 * inverse of {@link splitPartsForLayout} and the canonical way to read an
 * animal's layout (there is no free-standing `Animal.splitType` source of
 * truth). Presence-based over the multiset of `splitPart`s, so it works on a
 * partial slot set (during occupancy) and a transient over-full set (a claim
 * landing alongside open placeholders): a `whole` dominates, `half` +
 * `quarter` together are the unique `half_and_two_quarters` signature, and an
 * empty or unrecognized set yields `undefined`. Case-insensitive; null/unknown
 * parts are ignored.
 * @param splits - The animal's live Split docs (only `splitPart` is read).
 * @returns The derived whole-animal layout, or `undefined` when unrecognizable.
 */
export function deriveLayoutFromSplits(
  splits: ReadonlyArray<{ splitPart?: string | null }>,
): AnimalSplitType | undefined {
  let whole = 0;
  let half = 0;
  let quarter = 0;
  for (const split of splits) {
    switch (
      typeof split?.splitPart === 'string' ? split.splitPart.toLowerCase() : ''
    ) {
      case 'whole':
        whole += 1;
        break;
      case 'half':
        half += 1;
        break;
      case 'quarter':
        quarter += 1;
        break;
      default:
        break;
    }
  }

  if (whole > 0) return 'whole';
  if (half > 0 && quarter > 0) return 'half_and_two_quarters';
  if (quarter > 0) return 'quarters';
  if (half > 0) return 'half';
  return undefined;
}
